import { type HTMLAttributes, forwardRef } from 'react';

export interface ProgressRingProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Progress value from 0 to 100 */
  progress: number;
  /** Diameter of the ring in pixels */
  size?: number;
  /** Thickness of the ring stroke */
  strokeWidth?: number;
  /** Color of the progress arc (CSS color value) */
  color?: string;
  /** Label text displayed in the center (defaults to percentage) */
  label?: string;
}

export const ProgressRing = forwardRef<HTMLDivElement, ProgressRingProps>(
  (
    {
      progress,
      size = 80,
      strokeWidth = 8,
      color = 'var(--color-primary)',
      label,
      className = '',
      ...props
    },
    ref
  ) => {
    const clampedProgress = Math.min(100, Math.max(0, progress));
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (clampedProgress / 100) * circumference;

    return (
      <div
        ref={ref}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(clampedProgress)}
        aria-label={label ?? `${Math.round(clampedProgress)}%`}
        className={['relative inline-flex items-center justify-center', className].join(' ')}
        style={{ width: size, height: size }}
        {...props}
      >
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
          aria-hidden="true"
        >
          {/* Track circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--color-border)"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-[stroke-dashoffset] duration-default ease-ease-default"
          />
        </svg>
        {/* Center text */}
        <span className="absolute inset-0 flex items-center justify-center text-sm font-medium text-text-primary pointer-events-none">
          {label ?? `${Math.round(clampedProgress)}%`}
        </span>
      </div>
    );
  }
);

ProgressRing.displayName = 'ProgressRing';
